import type { ComponentType } from 'react';
import {
  UsersIcon,
  GitBranchIcon,
  ChartBarIcon,
  ScalesIcon,
  BookOpenTextIcon,
  BuildingsIcon,
  GraduationCapIcon,
  PlugsIcon,
} from '@phosphor-icons/react';

import { teamPath } from '@/contexts/space-context';

export interface SettingsNavItem {
  /** Display label. */
  label: string;
  /** URL slug appended to the settings base (e.g. "team/general"). */
  slug: string;
  /** Icon component (Phosphor). */
  icon: ComponentType<{
    size?: number;
    weight?: 'regular' | 'duotone' | 'fill' | 'bold' | 'thin' | 'light';
    className?: string;
  }>;
  /** Optional one-line description shown in some variants. */
  description?: string;
  /** Render the section full-width with its own header instead of the centered 720px column. */
  wide?: boolean;
}

/** Where a group shows up: personal space, and team spaces (all roles or admins only). */
interface SettingsNavVisibility {
  personal: boolean;
  team: false | 'all' | 'admin';
}

interface SettingsNavGroup {
  id: string;
  label: string;
  visibility: SettingsNavVisibility;
  items: SettingsNavItem[];
}

/** The space the settings shell is currently scoped to. */
export interface SpaceAccess {
  isPersonal: boolean;
  isAdmin: boolean;
}

export const SETTINGS_NAV_GROUPS: SettingsNavGroup[] = [
  {
    id: 'team',
    label: 'Team',
    visibility: { personal: false, team: 'admin' },
    items: [
      {
        label: 'General',
        slug: 'team/general',
        icon: BuildingsIcon,
        description: 'Settings that apply to the whole team.',
      },
      {
        label: 'Members',
        slug: 'team/members',
        icon: UsersIcon,
        description: 'Invite people, manage their roles, and track pending invitations.',
        wide: true,
      },
    ],
  },
  {
    id: 'automation',
    label: 'Automation',
    visibility: { personal: true, team: 'all' },
    items: [
      {
        label: 'Playbooks',
        slug: 'automation/playbooks',
        icon: BookOpenTextIcon,
        description: 'Reusable prompts that tell agents how to handle common tasks.',
        wide: true,
      },
      {
        label: 'Skills',
        slug: 'automation/skills',
        icon: GraduationCapIcon,
        description: 'Procedures agents load on demand when a task matches.',
        wide: true,
      },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    visibility: { personal: true, team: 'admin' },
    items: [
      {
        label: 'Source control',
        slug: 'integrations/source-control',
        icon: GitBranchIcon,
        description: 'Connect the service that hosts your code and manage your repositories.',
      },
      {
        label: 'Connectors',
        slug: 'integrations/connectors',
        icon: PlugsIcon,
        description: 'Give agents tools from outside the repository, without handing over credentials.',
        wide: true,
      },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    visibility: { personal: true, team: 'admin' },
    items: [
      {
        label: 'Usage',
        slug: 'insights/usage',
        icon: ChartBarIcon,
        description: 'What agent sessions are costing, and the budget they run under.',
        wide: true,
      },
    ],
  },
  {
    id: 'security',
    label: 'Security',
    visibility: { personal: true, team: 'admin' },
    items: [
      {
        label: 'Compliance',
        slug: 'security/compliance',
        icon: ScalesIcon,
        description: 'How your code and data are protected, and who to contact about security.',
      },
    ],
  },
];

export const DEFAULT_LANDING_SLUG = 'automation/playbooks';

/** URL prefix for the settings routes of a given space. */
export function settingsBase(teamId: string): string {
  return teamPath(teamId, '/settings');
}

export function isGroupVisible(group: SettingsNavGroup, { isPersonal, isAdmin }: SpaceAccess): boolean {
  if (isPersonal) return group.visibility.personal;
  if (group.visibility.team === false) return false;
  return group.visibility.team === 'all' || isAdmin;
}

export function visibleNavGroups(access: SpaceAccess): SettingsNavGroup[] {
  return SETTINGS_NAV_GROUPS.filter((group) => isGroupVisible(group, access));
}

export function isSlugVisible(slug: string, access: SpaceAccess): boolean {
  const group = SETTINGS_NAV_GROUPS.find((g) => g.items.some((i) => i.slug === slug));
  return group ? isGroupVisible(group, access) : true;
}
